// shop/router.js — состояния окон в адресной строке
// =============================================================================
// Каждое окно можно прислать ссылкой, без «#» и «?»:
//
//   /contact/                        форма связи
//   /catalog/<slug>/                 карточка товара
//   /catalog/<slug>/select/          выбор экземпляра
//   /catalog/<slug>/corporate/       корпоративный заказ
//   /catalog/<slug>/masterclass/     мастер-класс
//   /catalog/<slug>/contact/         связь по этому изделию
//   /catalog/<slug>/<id>/            конкретный экземпляр
//
// Страницы под все эти адреса генерируются на сборке (dev/html/catalog-routes.js),
// поэтому ссылка открывается напрямую, а не только из приложения. Здесь —
// вторая половина: при открытии и закрытии окна адрес меняется без перезагрузки,
// а кнопка «назад» возвращает предыдущее состояние.

let deps = { openPicker: null, closePicker: null, openContact: null, closeContact: null };
let suppress = false;   // не реагировать на собственные же изменения адреса

function currentBase() {
  // База — адрес страницы без хвоста состояния.
  const parts = location.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  if (parts[0] !== 'catalog' || parts.length < 2) return '/';
  return '/catalog/' + parts[1] + '/';
}

export function push(path) {
  if (suppress) return;
  if (location.pathname === path) return;
  try {
    history.pushState({ smola: true }, '', path);
  } catch (e) {
    // file:// и прочие экзотические случаи — просто не трогаем адрес
  }
}

// Страница под текущим адресом: если открыли ссылку сразу на окно, «под ним»
// лежит витрина или карточка товара — туда и возвращаемся при закрытии.
function basePage() {
  const parts = location.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  if (parts.length === 1 && parts[0] === 'contact') return '/';
  if (parts[0] === 'catalog' && parts.length >= 3) return '/catalog/' + parts[1] + '/';
  return location.pathname || '/';
}

// Куда вернуться при закрытии окна: окно открывают и с витрины, и с карточки,
// и «назад» должно вести на ту страницу, с которой пришли.
let returnTo = null;

// state: 'select' | 'corporate' | 'masterclass' | 'contact' | 'article' | 'none'
// opts.slug — товар, к которому относится окно. Без него база бралась из
// текущего адреса, и на витрине получался несуществующий /select/.
export function pushState(state, opts) {
  const options = opts || {};

  if (state === 'none') {
    const back = returnTo || basePage();
    returnTo = null;
    return push(back);
  }

  if (!returnTo) returnTo = basePage();

  if (state === 'contact' && !options.slug) return push('/contact/');

  const base = options.slug ? '/catalog/' + options.slug + '/' : currentBase();
  if (base === '/') return push('/');   // товар неизвестен — адрес не выдумываем

  if (state === 'article') return push(base + options.value + '/');
  return push(base + state + '/');
}

// Состояние, с которым страница открыта: его расставляет генератор
// в data-атрибутах <body>.
function initialState() {
  const body = document.body;
  if (body.hasAttribute('data-open-contact')) return { kind: 'contact' };

  const open = body.getAttribute('data-picker-open');
  if (!open) return null;

  if (open === 'article') {
    return { kind: 'article', slug: body.getAttribute('data-product'), id: body.getAttribute('data-picker-article') };
  }
  return { kind: open, slug: body.getAttribute('data-product') };
}

function apply(state) {
  if (!state) {
    if (deps.closePicker) deps.closePicker();
    if (deps.closeContact) deps.closeContact();
    return;
  }

  if (state.kind === 'contact' && !state.slug) {
    if (deps.openContact) deps.openContact();
    return;
  }

  if (deps.openPicker) deps.openPicker(state);
}

// Разбор адреса при переходе «назад»/«вперёд».
function stateFromPath() {
  const parts = location.pathname.replace(/\/+$/, '').split('/').filter(Boolean);

  if (parts.length === 1 && parts[0] === 'contact') return { kind: 'contact' };
  if (parts[0] !== 'catalog' || parts.length < 2) return null;

  const slug = parts[1];
  const tail = parts[2];
  if (!tail) return null;
  if (['select', 'corporate', 'masterclass', 'contact'].includes(tail)) return { kind: tail, slug };
  return { kind: 'article', slug, id: tail };
}

export function init(handlers) {
  deps = Object.assign(deps, handlers || {});

  // Состояние из адреса, с которым страница загрузилась.
  apply(initialState());

  window.addEventListener('popstate', () => {
    // Меняем окна, но не адрес — иначе история зациклится.
    suppress = true;
    apply(stateFromPath());
    suppress = false;
  });
}
